import React from 'react';
import { CLIMB_SPLIT_TYPES } from '../utils/indoorClimb/IndoorClimbUtil';
import ReportTypes from 'utils/ReportTypes';
import ActivityTypeMapper from 'utils/ActivityTypeMapper';

export function getFieldKey(field) {
    switch (field) {
        case 'ACTIVITY_TOTAL_ELEVATION_GAIN':
            return 'label_elevation_gain_total';
        case 'ACTIVITY_TRAINING_STRESS_SCORE':
            return 'label_training_stress_score';
        case 'ACTIVITY_AVG_DISTANCE':
            return 'label_average_distance';
        case 'ACTIVITY_MAX_DISTANCE':
            return 'gear_max_distance';
        case 'ACTIVITY_MAX_SPEED':
            return 'label_max_speed';
        case 'ACTIVITY_MAX_RUN_CADENCE':
            return 'label_max_run_cadence';
        case 'ACTIVITY_MAX_BIKE_CADENCE':
            return 'label_max_bike_cadence';
        case 'ACTIVITY_MAX_POWER':
            return 'label_max_power';
        case 'ACTIVITY_TOTAL_ELEVATION_LOSS':
            return 'course_map_elevation_loss';
        case 'ACTIVITY_AVERAGE_STRESS':
            return 'label_avg_stress';
        case 'ACTIVITY_MAX_STRESS':
            return 'label_max_stress';
        case 'ACTIVITY_AVERAGE_CADENCE':
            return 'label_average_cadence';
        case 'ACTIVITY_MAX_CADENCE':
            return 'label_max_cadence';
        case 'ACTIVITY_MOVING_DURATION':
            return 'label_moving_time';
        case 'ACTIVITY_TOTAL_ROUTES':
            return 'label_total_routes';
        case 'ACTIVITY_CLIMBING_TIME':
            return 'label_climbing_time';
        case 'ACTIVITY_TOTAL_ROUTE_ASCENT':
            return 'label_total_route_ascent';
        case 'ACTIVITY_MAX_ROUTE_ASCENT':
            return 'label_max_route_ascent';
        case 'ACTIVITY_ROUTES_ATTEMPTED':
            return 'label_routes_attempted';
        case 'ACTIVITY_ROUTES_COMPLETED':
            return 'label_routes_completed';
        case 'ACTIVITY_SENDS':
            return 'label_sends';
        case 'ACTIVITY_FALLS':
            return 'label_falls';
        case 'ACTIVITY_TOTAL_REST_TIME':
            return 'label_total_rest_time';
        case 'ACTIVITY_TOTAL_TIME':
            return 'label_time_total';
        case 'ACTIVITY_AVG_GAP':
            return 'avg_grade_adjusted_pace';
        case 'ACTIVITY_AVG_WHEELCHAIR_CADENCE':
            return 'label_avg_wheelchair_cadence';
        case 'ACTIVITY_MAX_WHEELCHAIR_CADENCE':
            return 'label_max_wheelchair_cadence';
        case 'ACTIVITY_AVG_PACK_WEIGHT':
            return 'label_average_pack_weight';
        case 'ACTIVITY_AVG_STEP_COUNT':
            return 'label_average_step_count';
        default:
            return field;
    }
}

export function getFieldNamespace(field) {
    switch (field) {
        case 'ACTIVITY_TOTAL_ELEVATION_GAIN':
        case 'ACTIVITY_TRAINING_STRESS_SCORE':
        case 'ACTIVITY_MAX_SPEED':
        case 'ACTIVITY_MAX_RUN_CADENCE':
        case 'ACTIVITY_MAX_BIKE_CADENCE':
        case 'ACTIVITY_MAX_POWER':
        case 'ACTIVITY_AVERAGE_STRESS':
        case 'ACTIVITY_MAX_STRESS':
        case 'ACTIVITY_AVERAGE_CADENCE':
        case 'ACTIVITY_MAX_CADENCE':
        case 'ACTIVITY_MOVING_DURATION':
        case 'ACTIVITY_TOTAL_ROUTE_ASCENT':
        case 'ACTIVITY_MAX_ROUTE_ASCENT':
        case 'ACTIVITY_TOTAL_TIME':
        case 'ACTIVITY_AVG_WHEELCHAIR_CADENCE':
        case 'ACTIVITY_MAX_WHEELCHAIR_CADENCE':
        case 'label_max_pace':
        case 'ACTIVITY_AVG_PACK_WEIGHT':
        case 'ACTIVITY_AVG_STEP_COUNT':
            return 'units';
        case 'ACTIVITY_AVG_DISTANCE':
            return 'golf_main';
        case 'ACTIVITY_MAX_DISTANCE':
            return 'gear_tracking';
        case 'ACTIVITY_TOTAL_ELEVATION_LOSS':
            return 'course';
        case 'ACTIVITY_TOTAL_ROUTES':
        case 'ACTIVITY_CLIMBING_TIME':
        case 'ACTIVITY_ROUTES_ATTEMPTED':
        case 'ACTIVITY_ROUTES_COMPLETED':
        case 'ACTIVITY_SENDS':
        case 'ACTIVITY_FALLS':
        case 'ACTIVITY_TOTAL_REST_TIME':
            return 'activity';
        case 'ACTIVITY_AVG_GAP':
            return 'gap';
        default:
            return 'reportswidget';
    }
}

export function personalizeMetricValue(key, value, { i18next, Personalizer, DateFormatter }, activityType) {
    const shouldPassType = activityType ? shouldPassActivityType(activityType) : false;
    switch (key) {
        case 'FULL_DATE':
            return valueUnit(Personalizer.personalizeDate(value));
        case 'MONTH_YEAR':
            return valueUnit(DateFormatter.formatMonthYear(value));
        case 'YEAR':
            return valueUnit(value.getFullYear());
        case 'ACTIVITY_TOTAL_DISTANCE':
        case 'ACTIVITY_AVG_DISTANCE':
        case 'ACTIVITY_MEDIAN_DISTANCE':
        case 'ACTIVITY_MAX_DISTANCE':
            return valueUnit(
                shouldPassType
                    ? Personalizer.personalizeDistance(value / 100, activityType)
                    : Personalizer.personalizeDistance(value / 100, 'all'),
                shouldPassType
                    ? i18next.t(`units:${Personalizer.getDistanceUnitKey(activityType)}`)
                    : i18next.t(`units:${Personalizer.getDistanceUnitKey()}`)
            );
        case 'ACTIVITY_AVERAGE_SPEED':
        case 'ACTIVITY_MAX_SPEED':
            return valueUnit(
                shouldPassType
                    ? Personalizer.personalizeSpeed(value * 10, activityType)
                    : Personalizer.personalizeSpeed(value * 10),
                shouldPassType
                    ? i18next.t(`units:${Personalizer.getSpeedUnitKey(activityType)}`)
                    : i18next.t(`units:${Personalizer.getSpeedUnitKey()}`)
            );
        case 'ACTIVITY_TOTAL_ELEVATION_GAIN':
        case 'ACTIVITY_AVG_ELEVATION_GAIN':
        case 'ACTIVITY_MEDIAN_ELEVATION_GAIN':
        case 'ACTIVITY_MAX_ELEVATION_GAIN':
            return valueUnit(
                Personalizer.personalizeElevation(value / 100),
                i18next.t(`units:${Personalizer.getElevationUnitKey()}`)
            );

        case 'ACTIVITY_TOTAL_ROUTE_ASCENT':
        case 'ACTIVITY_MAX_ROUTE_ASCENT':
            return valueUnit(
                Personalizer.personalizeElevation(value),
                i18next.t(`units:${Personalizer.getElevationUnitKey()}`)
            );
        case 'ACTIVITY_CLIMBING_TIME':
        case 'ACTIVITY_TOTAL_REST_TIME':
            return valueUnit(
                Personalizer.personalizeDuration(value),
                i18next.t(`units:${Personalizer.getDurationUnitKey()}`)
            );
        case 'ACTIVITY_TOTAL_DURATION':
        case 'ACTIVITY_MOVING_DURATION':
        case 'ACTIVITY_AVG_TIME':
        case 'ACTIVITY_MEDIAN_TIME':
        case 'ACTIVITY_MAX_TIME':
        case 'ACTIVITY_TOTAL_TIME':
            return valueUnit(
                Personalizer.personalizeDuration(value / 1000),
                i18next.t(`units:${Personalizer.getDurationUnitKey()}`)
            );
        case 'ACTIVITY_AVG_STRIDE_LENGTH':
            return valueUnit(
                Personalizer.personalizeAvgStrideLength(value),
                i18next.t(`units:${Personalizer.getAvgStrideLengthUnitKey()}`)
            );
        case 'ACTIVITY_AVERAGE_POWER':
        case 'ACTIVITY_MAX_POWER':
            return valueUnit(
                Personalizer.personalizePower(value),
                i18next.t(`units:${Personalizer.getPowerUnitKey()}`)
            );
        case 'ACTIVITY_AVERAGE_PACE':
        case 'label_max_pace':
        case 'ACTIVITY_AVG_GAP':
            return valueUnit(
                Personalizer.formatPace(60 / Personalizer.convertSpeed(value * 10)),
                i18next.t(`units:${Personalizer.getPaceUnitKey()}`)
            );
        case 'ACTIVITY_ACTIVE_CALORIES':
            return valueUnit(Personalizer.personalizeCalories(value));
        case 'ACTIVITY_MAX_HEART_RATE':
        case 'ACTIVITY_AVERAGE_HEART_RATE':
            return valueUnit(
                Personalizer.personalizeHeartRate(value),
                i18next.t(`units:${Personalizer.getHeartRateUnitKey()}`)
            );
        case 'ACTIVITY_GROUND_CONTACT_TIME':
            return valueUnit(
                Personalizer.personalizeGroundContactTime(value),
                i18next.t(`units:${Personalizer.getGroundContactTimeUnitKey()}`)
            );
        case 'ACTIVITY_AVERAGE_RUN_CADENCE':
        case 'ACTIVITY_MAX_RUN_CADENCE':
            return valueUnit(
                Personalizer.personalizeCadence(value),
                i18next.t(`units:${Personalizer.getCadenceUnitKey('running')}`)
            );
        case 'ACTIVITY_AVERAGE_CADENCE':
        case 'ACTIVITY_MAX_CADENCE':
        case 'ACTIVITY_AVERAGE_BIKE_CADENCE':
        case 'ACTIVITY_MAX_BIKE_CADENCE':
            return valueUnit(
                Personalizer.personalizeCadence(value),
                i18next.t(`units:${Personalizer.getCadenceUnitKey()}`)
            );
        case 'ACTIVITY_VO2_MAX':
        case 'ACTIVITY_VO2_MAX_CYCLING':
            return valueUnit(Personalizer.personalizeVO2Max(value));
        case 'ACTIVITY_AVERAGE_SWIM_PACE':
            return valueUnit(
                Personalizer.personalizeSwimPace(value),
                i18next.t(`units:${Personalizer.getSwimPaceUnitKey()}`)
            );
        case 'ACTIVITY_AVG_VERTICAL_RATIO':
            return valueUnit(
                Personalizer.personalizeVerticalRatio(value),
                i18next.t(`units:${Personalizer.getVerticalRatioUnitKey()}`)
            );
        case 'ACTIVITY_VERTICAL_OSCILLATION':
            return valueUnit(
                Personalizer.personalizeVerticalOscillation(value),
                i18next.t(`units:${Personalizer.getVerticalOscillationUnitKey()}`)
            );
        case 'ACTIVITY_TOTAL_ELEVATION_LOSS':
        case 'ACTIVITY_AVG_ELEVATION_LOSS':
        case 'ACTIVITY_MEDIAN_ELEVATION_LOSS':
        case 'ACTIVITY_MAX_ELEVATION_LOSS':
            return valueUnit(
                Personalizer.personalizeElevation(value / 100),
                i18next.t(`units:${Personalizer.getElevationUnitKey()}`)
            );
        case 'ACTIVITY_AVG_GROUND_CONTACT_BALANCE':
            return valueUnit(Personalizer.personalizeGroundContactTime(value));
        case 'ACTIVITY_AVG_WHEELCHAIR_CADENCE':
        case 'ACTIVITY_MAX_WHEELCHAIR_CADENCE':
            return valueUnit(value, i18next.t(`units:${Personalizer.getCadenceUnitKey('wheelchair_push_run')}`));
        case 'ACTIVITY_AVERAGE_STRESS':
        case 'ACTIVITY_MAX_STRESS':
            return valueUnit(Personalizer.personalizeStress(value));
        case 'ACTIVITY_AVG_PACK_WEIGHT':
            return valueUnit(
                Personalizer.personalizeWeight(value, Personalizer.NumberFormats.NO_DECIMAL),
                i18next.t(`units:${Personalizer.getWeightUnitKey()}`)
            );
        case 'ACTIVITY_AVG_STEP_COUNT':
            return valueUnit(Personalizer.formatWholeNumber(value));
        default:
            return valueUnit(value);
    }
}

export const indoorClimbSplitSummariesMapper = {
    ACTIVITY_TOTAL_ROUTES: {
        requestMetric: 'splitSummaries.noOfSplits.CLIMB_ACTIVE',
        summaryType: CLIMB_SPLIT_TYPES.CLIMB_ACTIVE,
        statsName: 'splitSummaries.noOfSplits'
    },
    ACTIVITY_CLIMBING_TIME: {
        requestMetric: 'splitSummaries.duration.CLIMB_ACTIVE',
        summaryType: CLIMB_SPLIT_TYPES.CLIMB_ACTIVE,
        statsName: 'splitSummaries.duration'
    },
    ACTIVITY_TOTAL_ROUTE_ASCENT: {
        requestMetric: 'splitSummaries.totalAscent.CLIMB_ACTIVE',
        summaryType: CLIMB_SPLIT_TYPES.CLIMB_ACTIVE,
        statsName: 'splitSummaries.totalAscent'
    },
    ACTIVITY_MAX_ROUTE_ASCENT: {
        requestMetric: 'splitSummaries.maxElevationGain.CLIMB_ACTIVE',
        summaryType: CLIMB_SPLIT_TYPES.CLIMB_ACTIVE,
        statsName: 'splitSummaries.maxElevationGain'
    },
    ACTIVITY_ROUTES_ATTEMPTED: {
        requestMetric: 'splitSummaries.numClimbsAttempted.CLIMB_ACTIVE',
        summaryType: CLIMB_SPLIT_TYPES.CLIMB_ACTIVE,
        statsName: 'splitSummaries.numClimbsAttempted'
    },
    ACTIVITY_ROUTES_COMPLETED: {
        requestMetric: 'splitSummaries.numClimbsCompleted.CLIMB_ACTIVE',
        summaryType: CLIMB_SPLIT_TYPES.CLIMB_ACTIVE,
        statsName: 'splitSummaries.numClimbsCompleted'
    },
    ACTIVITY_SENDS: {
        requestMetric: 'splitSummaries.numClimbSends.CLIMB_ACTIVE',
        summaryType: CLIMB_SPLIT_TYPES.CLIMB_ACTIVE,
        statsName: 'splitSummaries.numClimbSends'
    },
    ACTIVITY_FALLS: {
        requestMetric: 'splitSummaries.numFalls.CLIMB_ACTIVE',
        summaryType: CLIMB_SPLIT_TYPES.CLIMB_ACTIVE,
        statsName: 'splitSummaries.numFalls'
    },
    ACTIVITY_TOTAL_REST_TIME: {
        requestMetric: 'splitSummaries.duration.CLIMB_REST',
        summaryType: CLIMB_SPLIT_TYPES.CLIMB_REST,
        statsName: 'splitSummaries.duration'
    }
};

function metricProp(metric, prop) {
    return {
        metric: metric,
        property: prop
    };
}

function valueUnit(value, unit) {
    return {
        value: value,
        unit: unit
    };
}

/**
 * Certain activity types require different values for Distance/Speed
 * for which Personalizer utilizes. For example, Boating displays Distance
 * in Nautical Miles but Windsurfing would show Distance in Miles/Kilometers.
 * Another example is both of these types would need to show the Speed values
 * in Knots.
 * @param activityType the activity type we are getting the exception for.
 */
function shouldPassActivityType(activityType) {
    switch (activityType) {
        case ActivityTypeMapper.Windsurfing:
        case ActivityTypeMapper.WindsurfingV2:
        case 'boating':
        case ActivityTypeMapper.Boating: //v2
            return true;
        default:
            return false;
    }
}

const VISIBLE_FIELDS_METRICS_MAP = {
    ACTIVITY_TOTAL_DISTANCE: metricProp('distance', 'sum'),
    ACTIVITY_AVG_DISTANCE: metricProp('distance', 'avg'),
    ACTIVITY_MAX_DISTANCE: metricProp('distance', 'max'),
    // 'ACTIVITY_MEDIAN_DISTANCE',
    ACTIVITY_NUMBER_OF_ACTIVITIES: metricProp('duration', 'count'),
    ACTIVITY_TOTAL_DURATION: metricProp('duration', 'sum'),
    ACTIVITY_MOVING_DURATION: metricProp('movingDuration', 'sum'),
    ACTIVITY_AVG_TIME: metricProp('duration', 'avg'),
    ACTIVITY_MAX_TIME: metricProp('duration', 'max'),
    //'ACTIVITY_MEDIAN_TIME',
    ACTIVITY_ACTIVE_CALORIES: metricProp('calories', 'sum'),
    ACTIVITY_TOTAL_ELEVATION_GAIN: metricProp('elevationGain', 'sum'),
    ACTIVITY_AVG_ELEVATION_GAIN: metricProp('elevationGain', 'avg'),
    ACTIVITY_MAX_ELEVATION_GAIN: metricProp('elevationGain', 'max'),
    //'ACTIVITY_MEDIAN_ELEVATION_GAIN', ''),
    ACTIVITY_TOTAL_ELEVATION_LOSS: metricProp('elevationLoss', 'sum'),
    ACTIVITY_AVG_ELEVATION_LOSS: metricProp('elevationLoss', 'avg'),
    ACTIVITY_MAX_ELEVATION_LOSS: metricProp('elevationLoss', 'max'),
    //'ACTIVITY_MEDIAN_ELEVATION_LOSS': metricProp('elevationLoss', ''),
    ACTIVITY_AVERAGE_SPEED: metricProp('avgSpeed', 'avg'),
    ACTIVITY_MAX_SPEED: metricProp('maxSpeed', 'max'),
    ACTIVITY_AVERAGE_PACE: metricProp('avgSpeed', 'avg'),
    label_max_pace: metricProp('maxSpeed', 'max'),
    ACTIVITY_AVERAGE_HEART_RATE: metricProp('avgHr', 'avg'),
    ACTIVITY_MAX_HEART_RATE: metricProp('maxHr', 'max'),
    ACTIVITY_AVERAGE_RUN_CADENCE: metricProp('avgRunCadence', 'avg'),
    ACTIVITY_MAX_RUN_CADENCE: metricProp('maxRunCadence', 'max'),
    ACTIVITY_AVERAGE_BIKE_CADENCE: metricProp('avgBikeCadence', 'avg'),
    ACTIVITY_MAX_BIKE_CADENCE: metricProp('maxBikeCadence', 'max'),
    ACTIVITY_AVG_WHEELCHAIR_CADENCE: metricProp('avgWheelchairCadence', 'avg'),
    ACTIVITY_MAX_WHEELCHAIR_CADENCE: metricProp('maxWheelchairCadence', 'max'),
    ACTIVITY_AVERAGE_POWER: metricProp('avgPower', 'avg'),
    ACTIVITY_MAX_POWER: metricProp('maxPower', 'max'),
    ACTIVITY_VERTICAL_OSCILLATION: metricProp('avgVerticalOscillation', 'avg'),
    ACTIVITY_GROUND_CONTACT_TIME: metricProp('avgGroundContactTime', 'avg'),
    ACTIVITY_AVG_STRIDE_LENGTH: metricProp('avgStrideLength', 'avg'),
    ACTIVITY_AVERAGE_STRESS: metricProp('avgStress', 'avg'),
    ACTIVITY_MAX_STRESS: metricProp('maxStress', 'max'),
    ACTIVITY_AVERAGE_CADENCE: metricProp('avgBikeCadence', 'avg'),
    ACTIVITY_MAX_CADENCE: metricProp('maxBikeCadence', 'max'),
    ACTIVITY_TOTAL_ROUTES: metricProp(indoorClimbSplitSummariesMapper.ACTIVITY_TOTAL_ROUTES.requestMetric, 'sum'),
    ACTIVITY_CLIMBING_TIME: metricProp(indoorClimbSplitSummariesMapper.ACTIVITY_CLIMBING_TIME.requestMetric, 'sum'),
    ACTIVITY_TOTAL_ROUTE_ASCENT: metricProp(
        indoorClimbSplitSummariesMapper.ACTIVITY_TOTAL_ROUTE_ASCENT.requestMetric,
        'sum'
    ),
    ACTIVITY_MAX_ROUTE_ASCENT: metricProp(
        indoorClimbSplitSummariesMapper.ACTIVITY_MAX_ROUTE_ASCENT.requestMetric,
        'max'
    ),
    ACTIVITY_ROUTES_ATTEMPTED: metricProp(
        indoorClimbSplitSummariesMapper.ACTIVITY_ROUTES_ATTEMPTED.requestMetric,
        'sum'
    ),
    ACTIVITY_ROUTES_COMPLETED: metricProp(
        indoorClimbSplitSummariesMapper.ACTIVITY_ROUTES_COMPLETED.requestMetric,
        'sum'
    ),
    ACTIVITY_SENDS: metricProp(indoorClimbSplitSummariesMapper.ACTIVITY_SENDS.requestMetric, 'sum'),
    ACTIVITY_FALLS: metricProp(indoorClimbSplitSummariesMapper.ACTIVITY_FALLS.requestMetric, 'sum'),
    ACTIVITY_TOTAL_REST_TIME: metricProp(indoorClimbSplitSummariesMapper.ACTIVITY_TOTAL_REST_TIME.requestMetric, 'sum'),
    ACTIVITY_TOTAL_TIME: metricProp('duration', 'sum'),
    ACTIVITY_AVG_GAP: metricProp('avgGradeAdjustedSpeed', 'avg'),
    ACTIVITY_AVG_PACK_WEIGHT: metricProp('beginPackWeight', 'avg'),
    ACTIVITY_AVG_STEP_COUNT: metricProp('steps', 'avg')
};

const VISIBLE_FIELDS = [
    'ACTIVITY_NUMBER_OF_ACTIVITIES',
    'ACTIVITY_TOTAL_DISTANCE',
    'ACTIVITY_TOTAL_DURATION',
    'ACTIVITY_TOTAL_ELEVATION_GAIN',
    'ACTIVITY_AVERAGE_SPEED',
    'ACTIVITY_AVERAGE_HEART_RATE',
    'ACTIVITY_AVERAGE_RUN_CADENCE',
    'ACTIVITY_AVERAGE_BIKE_CADENCE',
    'ACTIVITY_ACTIVE_CALORIES'
];

const ESPORT_VISIBLE_FIELDS = [
    'ACTIVITY_NUMBER_OF_ACTIVITIES',
    'ACTIVITY_TOTAL_DURATION',
    'ACTIVITY_AVERAGE_STRESS',
    'ACTIVITY_AVERAGE_HEART_RATE',
    'ACTIVITY_MAX_STRESS'
];

const ONSHOREGRINDING_VISIBLE_FIELDS = [
    'ACTIVITY_TOTAL_DURATION',
    'ACTIVITY_MOVING_DURATION',
    'ACTIVITY_AVERAGE_HEART_RATE',
    'ACTIVITY_MAX_HEART_RATE',
    'ACTIVITY_AVERAGE_POWER',
    'ACTIVITY_MAX_POWER',
    'ACTIVITY_ACTIVE_CALORIES',
    'ACTIVITY_TOTAL_DISTANCE',
    'ACTIVITY_AVERAGE_CADENCE',
    'ACTIVITY_MAX_CADENCE'
];

const WINDSURFING_VISIBLE_FIELDS = [
    'ACTIVITY_NUMBER_OF_ACTIVITIES',
    'ACTIVITY_TOTAL_DISTANCE',
    'ACTIVITY_TOTAL_DURATION',
    'ACTIVITY_ACTIVE_CALORIES',
    'ACTIVITY_AVERAGE_SPEED',
    'ACTIVITY_MAX_SPEED',
    'ACTIVITY_AVERAGE_HEART_RATE',
    'ACTIVITY_TOTAL_ELEVATION_GAIN'
];

const INDOORCLIMBING_VISIBLE_FIELDS = [
    'ACTIVITY_TOTAL_ROUTES',
    'ACTIVITY_CLIMBING_TIME',
    'ACTIVITY_TOTAL_ROUTE_ASCENT',
    'ACTIVITY_MAX_ROUTE_ASCENT',
    'ACTIVITY_ROUTES_ATTEMPTED',
    'ACTIVITY_ROUTES_COMPLETED',
    'ACTIVITY_SENDS',
    'ACTIVITY_FALLS',
    'ACTIVITY_ACTIVE_CALORIES',
    'ACTIVITY_AVERAGE_HEART_RATE',
    'ACTIVITY_MAX_HEART_RATE',
    'ACTIVITY_TOTAL_REST_TIME',
    'ACTIVITY_TOTAL_TIME'
];

const RUNNING_VISIBLE_FIELDS = [
    'ACTIVITY_NUMBER_OF_ACTIVITIES',
    'ACTIVITY_TOTAL_DISTANCE',
    'ACTIVITY_AVG_DISTANCE',
    'ACTIVITY_MAX_DISTANCE',
    'ACTIVITY_TOTAL_DURATION',
    'ACTIVITY_ACTIVE_CALORIES',
    'ACTIVITY_TOTAL_ELEVATION_GAIN',
    'ACTIVITY_AVG_ELEVATION_GAIN',
    'ACTIVITY_MAX_ELEVATION_GAIN',
    'ACTIVITY_TOTAL_ELEVATION_LOSS',
    'ACTIVITY_AVG_ELEVATION_LOSS',
    'ACTIVITY_MAX_ELEVATION_LOSS',
    'ACTIVITY_AVERAGE_PACE',
    'ACTIVITY_AVG_GAP',
    'label_max_pace',
    'ACTIVITY_AVERAGE_HEART_RATE',
    'ACTIVITY_MAX_HEART_RATE',
    'ACTIVITY_AVERAGE_RUN_CADENCE',
    'ACTIVITY_MAX_RUN_CADENCE',
    'ACTIVITY_VERTICAL_OSCILLATION',
    'ACTIVITY_GROUND_CONTACT_TIME',
    'ACTIVITY_AVG_STRIDE_LENGTH'
];

const PARASPORTS_VISIBLE_FIELDS = [
    'ACTIVITY_NUMBER_OF_ACTIVITIES',
    'ACTIVITY_TOTAL_DISTANCE',
    'ACTIVITY_AVG_DISTANCE',
    'ACTIVITY_MAX_DISTANCE',
    'ACTIVITY_TOTAL_DURATION',
    'ACTIVITY_ACTIVE_CALORIES',
    'ACTIVITY_TOTAL_ELEVATION_GAIN',
    'ACTIVITY_AVG_ELEVATION_GAIN',
    'ACTIVITY_MAX_ELEVATION_GAIN',
    'ACTIVITY_TOTAL_ELEVATION_LOSS',
    'ACTIVITY_AVG_ELEVATION_LOSS',
    'ACTIVITY_MAX_ELEVATION_LOSS',
    'ACTIVITY_AVERAGE_PACE',
    'ACTIVITY_AVG_GAP',
    'label_max_pace',
    'ACTIVITY_AVERAGE_HEART_RATE',
    'ACTIVITY_MAX_HEART_RATE',
    'ACTIVITY_AVG_WHEELCHAIR_CADENCE',
    'ACTIVITY_MAX_WHEELCHAIR_CADENCE'
];

const CYCLING_VISIBLE_FIELDS = [
    'ACTIVITY_NUMBER_OF_ACTIVITIES',
    'ACTIVITY_TOTAL_DISTANCE',
    'ACTIVITY_AVG_DISTANCE',
    'ACTIVITY_MAX_DISTANCE',
    'ACTIVITY_TOTAL_DURATION',
    'ACTIVITY_ACTIVE_CALORIES',
    'ACTIVITY_TOTAL_ELEVATION_GAIN',
    'ACTIVITY_AVG_ELEVATION_GAIN',
    'ACTIVITY_MAX_ELEVATION_GAIN',
    'ACTIVITY_TOTAL_ELEVATION_LOSS',
    'ACTIVITY_AVG_ELEVATION_LOSS',
    'ACTIVITY_MAX_ELEVATION_LOSS',
    'ACTIVITY_AVERAGE_SPEED',
    'ACTIVITY_MAX_SPEED',
    'ACTIVITY_AVERAGE_HEART_RATE',
    'ACTIVITY_MAX_HEART_RATE',
    'ACTIVITY_AVERAGE_BIKE_CADENCE',
    'ACTIVITY_MAX_BIKE_CADENCE',
    'ACTIVITY_AVERAGE_POWER',
    'ACTIVITY_MAX_POWER'
];

const HIKING_VISIBLE_FIELDS = [
    'ACTIVITY_NUMBER_OF_ACTIVITIES',
    'ACTIVITY_TOTAL_DISTANCE',
    'ACTIVITY_AVG_DISTANCE',
    'ACTIVITY_MAX_DISTANCE',
    'ACTIVITY_TOTAL_DURATION',
    'ACTIVITY_ACTIVE_CALORIES',
    'ACTIVITY_TOTAL_ELEVATION_GAIN',
    'ACTIVITY_AVG_ELEVATION_GAIN',
    'ACTIVITY_MAX_ELEVATION_GAIN',
    'ACTIVITY_TOTAL_ELEVATION_LOSS',
    'ACTIVITY_AVG_ELEVATION_LOSS',
    'ACTIVITY_MAX_ELEVATION_LOSS',
    'ACTIVITY_AVERAGE_PACE',
    'ACTIVITY_AVG_GAP',
    'label_max_pace',
    'ACTIVITY_AVERAGE_HEART_RATE',
    'ACTIVITY_MAX_HEART_RATE'
];

const RUCKING_VISIBLE_FIELDS = [
    'ACTIVITY_NUMBER_OF_ACTIVITIES',
    'ACTIVITY_TOTAL_DISTANCE',
    'ACTIVITY_AVG_DISTANCE',
    'ACTIVITY_MAX_DISTANCE',
    'ACTIVITY_TOTAL_DURATION',
    'ACTIVITY_ACTIVE_CALORIES',
    'ACTIVITY_TOTAL_ELEVATION_GAIN',
    'ACTIVITY_AVG_ELEVATION_GAIN',
    'ACTIVITY_MAX_ELEVATION_GAIN',
    'ACTIVITY_TOTAL_ELEVATION_LOSS',
    'ACTIVITY_AVG_ELEVATION_LOSS',
    'ACTIVITY_MAX_ELEVATION_LOSS',
    'ACTIVITY_AVERAGE_PACE',
    'ACTIVITY_AVG_GAP',
    'label_max_pace',
    'ACTIVITY_AVERAGE_HEART_RATE',
    'ACTIVITY_MAX_HEART_RATE',
    'ACTIVITY_AVG_PACK_WEIGHT'
];

const ALL_FIELDS = [
    'ACTIVITY_NUMBER_OF_ACTIVITIES',
    'ACTIVITY_TOTAL_DISTANCE',
    'ACTIVITY_AVG_DISTANCE',
    //'ACTIVITY_MEDIAN_DISTANCE',
    'ACTIVITY_MAX_DISTANCE',
    'ACTIVITY_TOTAL_DURATION',
    'ACTIVITY_MOVING_DURATION',
    'ACTIVITY_AVG_TIME',
    //'ACTIVITY_MEDIAN_TIME',
    'ACTIVITY_MAX_TIME',
    'ACTIVITY_TOTAL_ROUTES',
    'ACTIVITY_CLIMBING_TIME',
    'ACTIVITY_TOTAL_ROUTE_ASCENT',
    'ACTIVITY_MAX_ROUTE_ASCENT',
    'ACTIVITY_ROUTES_ATTEMPTED',
    'ACTIVITY_ROUTES_COMPLETED',
    'ACTIVITY_SENDS',
    'ACTIVITY_FALLS',
    'ACTIVITY_ACTIVE_CALORIES',
    'ACTIVITY_TOTAL_ELEVATION_GAIN',
    'ACTIVITY_AVG_ELEVATION_GAIN',
    //'ACTIVITY_MEDIAN_ELEVATION_GAIN',
    'ACTIVITY_MAX_ELEVATION_GAIN',
    'ACTIVITY_TOTAL_ELEVATION_LOSS',
    'ACTIVITY_AVG_ELEVATION_LOSS',
    //'ACTIVITY_MEDIAN_ELEVATION_LOSS',
    'ACTIVITY_MAX_ELEVATION_LOSS',
    'ACTIVITY_AVERAGE_SPEED',
    'ACTIVITY_MAX_SPEED',
    'ACTIVITY_AVERAGE_PACE',
    'ACTIVITY_AVG_GAP',
    'label_max_pace',
    'ACTIVITY_AVERAGE_HEART_RATE',
    'ACTIVITY_MAX_HEART_RATE',
    'ACTIVITY_AVERAGE_RUN_CADENCE',
    'ACTIVITY_MAX_RUN_CADENCE',
    'ACTIVITY_AVERAGE_BIKE_CADENCE',
    'ACTIVITY_MAX_BIKE_CADENCE',
    'ACTIVITY_AVG_WHEELCHAIR_CADENCE',
    'ACTIVITY_MAX_WHEELCHAIR_CADENCE',
    'ACTIVITY_AVERAGE_POWER',
    'ACTIVITY_MAX_POWER',
    'ACTIVITY_VERTICAL_OSCILLATION',
    'ACTIVITY_GROUND_CONTACT_TIME',
    'ACTIVITY_AVG_STRIDE_LENGTH',
    'ACTIVITY_AVERAGE_STRESS',
    'ACTIVITY_MAX_STRESS',
    'ACTIVITY_AVERAGE_CADENCE',
    'ACTIVITY_MAX_CADENCE',
    'ACTIVITY_TOTAL_REST_TIME',
    'ACTIVITY_TOTAL_TIME',
    'ACTIVITY_AVG_PACK_WEIGHT',
    'ACTIVITY_AVG_STEP_COUNT'
];
export function getAllFields() {
    return ALL_FIELDS;
}
export function getDefaultFields() {
    return VISIBLE_FIELDS;
}
export function getEsportDefaultFields() {
    return ESPORT_VISIBLE_FIELDS;
}
export function getOnShoreGrindingDefaultFields() {
    return ONSHOREGRINDING_VISIBLE_FIELDS;
}
export function getHiitDefaultFields() {
    let HIIT_VISIBLE_FIELDS = [...VISIBLE_FIELDS];
    const avgHeartRateIndex = VISIBLE_FIELDS.findIndex((field) => field === 'ACTIVITY_AVERAGE_HEART_RATE');

    // insert the Max Heart Rate field immediately after the Avg Heart Rate
    HIIT_VISIBLE_FIELDS.splice(avgHeartRateIndex + 1, 0, 'ACTIVITY_MAX_HEART_RATE');

    return HIIT_VISIBLE_FIELDS;
}
export function getWindsurfingDefaultFields() {
    return WINDSURFING_VISIBLE_FIELDS;
}

export function getIndoorClimbingDefaultFields() {
    return INDOORCLIMBING_VISIBLE_FIELDS;
}

export function getRunningDefaultFields() {
    return RUNNING_VISIBLE_FIELDS;
}

export function getCyclingDefaultFields() {
    return CYCLING_VISIBLE_FIELDS;
}

export function getParasportsDefaultFields() {
    return PARASPORTS_VISIBLE_FIELDS;
}

export function getHikingDefaultFields(activityType) {
    if (activityType === ActivityTypeMapper.Rucking) {
        return RUCKING_VISIBLE_FIELDS;
    }

    return HIKING_VISIBLE_FIELDS;
}

export function jsonFieldsToArray(selectedFields) {
    return selectedFields.map((field) => {
        return field.key;
    });
}
export function arrayToJsonFields(array) {
    return ALL_FIELDS.map((field) => {
        return {
            title: `${getFieldNamespace(field)}:${getFieldKey(field)}`,
            key: field,
            visible: array.indexOf(field) !== -1,
            align: 'right'
        };
    });
}
export function fieldsToMetrics(fields) {
    let metrics = [];
    fields.forEach((field) => {
        let metricProp = VISIBLE_FIELDS_METRICS_MAP[field];
        if (!metricProp) {
            return true;
        }
        if (metrics.indexOf(metricProp.metric) === -1) {
            metrics.push(metricProp.metric);
        }
    });
    return metrics;
}

export function getMetricProp(field) {
    return VISIBLE_FIELDS_METRICS_MAP[field];
}

// stats object from fitnessstats-service/activity
export function getActivityTypes(stats) {
    let activityTypes = [];
    for (let activityType in stats) {
        if (stats.hasOwnProperty(activityType)) {
            activityTypes.push(activityType);
        }
    }
    return activityTypes;
}

export function getParentActivityTypes() {
    return [
        'all',
        'running',
        'cycling',
        'multi_sport',
        'fitness_equipment',
        'hiking',
        'swimming',
        'walking',
        'transition',
        'motorcycling',
        'other',
        'diving',
        'yoga'
    ];
}

export function displayValueUnit(valueUnit) {
    if (valueUnit.unit) {
        return `${valueUnit.value} ${valueUnit.unit}`;
    } else {
        return valueUnit.value;
    }
}

export const MULTIPLE_DAYS_REQUEST_PERIOD_KEYS = {
    FOUR_WEEKS: 'fourWeeks',
    ONE_MONTH: 'oneMonth',
    SEVEN_DAYS: 'sevenDays'
};

export const NO_SIX_MONTHS_REPORTS = [
    ReportTypes.FITNESS_AGE,
    ReportTypes.SLEEP_SLEEP_SCORE,
    ReportTypes.WELLNESS_FLOORS_ASCENDED,
    ReportTypes.WELLNESS_MODERATE_INTENSITY_MINUTES,
    ReportTypes.WELLNESS_TOTAL_STEPS,
    ReportTypes.SLEEP_SLEEP_DURATION,
    ReportTypes.COMMON_TOTAL_CALORIES,
    ReportTypes.WELLNESS_HYDRATION,
    ReportTypes.WELLNESS_RESTING_HEART_RATE,
    ReportTypes.WELLNESS_AVERAGE_STRESS
];

/**
 * Provides a customized Dot shape (e.g., diamond, triangle, square) for use on a Recharts <Line /> element. Recharts
 * allows for pre-defined shapes on the legend (i.e., legendType), however, this does not chage the shape on line itself.
 * This method copies svg paths from the Recharts legend to be used as the "dot" prop for <Line /> Recharts components.
 * dotColor and dotShape should be provided at a minimum. See GamingStressReportChart.jsx for examples.
 */
export const CustomizedRechartsDot = (props) => {
    const { cx, cy, stroke, payload, value, dotColor, dotShape, dotWidth, dotHeight, strokeWidth, style } = props;

    if (value === null) return null; // if the value is null, we don't want any shape returned

    const width = dotWidth ? dotWidth : 14;
    const height = dotHeight ? dotHeight : 14;
    const color = dotColor ? dotColor : 'red'; // if no color provided, default to red for awareness
    const shape = dotShape ? dotShape : 'circle'; // if no shape provided, default to circle

    if (shape === 'diamond') {
        return (
            <svg
                x={cx - width / 2}
                y={cy - height / 2}
                width={width}
                height={height}
                fill={color}
                viewBox="0 0 32 32"
                style={style}
            >
                <path
                    fill={color}
                    stroke={stroke || 'none'}
                    strokeWidth={strokeWidth || 0}
                    cx="16"
                    cy="16"
                    type={shape}
                    className="recharts-symbols"
                    transform="translate(16, 16)"
                    d="M0,-16L9.237604307034012,0L0,16L-9.237604307034012,0Z"
                />
            </svg>
        );
    }

    if (shape === 'triangle') {
        return (
            <svg
                x={cx - width / 2}
                y={cy - height / 2}
                width={width}
                height={height}
                fill={color}
                viewBox="0 0 32 32"
                style={style}
            >
                <path
                    fill={color}
                    stroke={stroke || 'none'}
                    strokeWidth={strokeWidth || 0}
                    cx="16"
                    cy="16"
                    type={shape}
                    className="recharts-symbols"
                    transform="translate(16, 16)"
                    d="M0,-18.475208614068023L15.999999999999998,9.237604307034012L-15.999999999999998,9.237604307034012Z"
                />
            </svg>
        );
    }

    if (shape === 'square') {
        return (
            <svg
                x={cx - width / 2}
                y={cy - height / 2}
                width={width}
                height={height}
                fill={color}
                viewBox="0 0 32 32"
                style={style}
            >
                <path
                    fill={color}
                    stroke={stroke || 'none'}
                    strokeWidth={strokeWidth || 0}
                    cx="16"
                    cy="16"
                    type={shape}
                    className="recharts-symbols"
                    transform="translate(16, 16)"
                    d="M-16,-16h32v32h-32Z"
                />
            </svg>
        );
    }

    // else always return a circle, a default "Dot"
    return (
        <svg
            x={cx - width / 2}
            y={cy - height / 2}
            width={width}
            height={height}
            fill={color}
            viewBox="0 0 32 32"
            style={style}
        >
            <path
                fill={color}
                stroke={stroke || 'none'}
                strokeWidth={strokeWidth || 0}
                cx="16"
                cy="16"
                type={shape}
                className="recharts-symbols"
                transform="translate(16, 16)"
                d="M16,0A16,16,0,1,1,-16,0A16,16,0,1,1,16,0"
            />
        </svg>
    );
};
